/**
 * user-error.ts
 *
 * Converts caught errors and API error payloads into user-friendly messages.
 * End users should never see raw exception messages, Prisma codes, stack
 * traces, Stripe integration strings, or internal API error strings.
 */

type ErrorContext =
  | "auth"
  | "register"
  | "session"
  | "upload"
  | "payment"
  | "payment_form"
  | "message"
  | "profile"
  | "load"
  | "forum"
  | "booking"
  | "generic";

export const ACCOUNT_NOT_FOUND_CODE = "ACCOUNT_NOT_FOUND";
export const ACCOUNT_DELETED_CODE = "ACCOUNT_DELETED";

export function isAccountDeletedError(error: string): boolean {
  const lower = error.toLowerCase();
  return (
    error === ACCOUNT_DELETED_CODE ||
    lower.includes("account_deleted") ||
    lower.includes("has been deleted") ||
    lower.includes("account is no longer available") ||
    lower.includes("no longer available")
  );
}

/** Stable authorize() codes plus older NextAuth strings that mean the same thing. */
export function isAccountNotFoundError(error: string): boolean {
  if (isAccountDeletedError(error)) return false;
  const lower = error.toLowerCase();
  return (
    error === ACCOUNT_NOT_FOUND_CODE ||
    lower.includes("no account found") ||
    lower.includes("account does not exist")
  );
}

export function isCredentialMismatchError(error: string): boolean {
  const lower = error.toLowerCase();
  return (
    error === "CredentialsSignin" ||
    error === "Invalid credentials" ||
    lower.includes("incorrect password") ||
    lower.includes("incorrect email or password")
  );
}

const FRIENDLY: Record<ErrorContext, string> = {
  auth:         "Something went wrong logging in. Please try again.",
  register:     "We couldn't create your account. Please try again.",
  session:      "We couldn't complete that action. Please try again.",
  upload:       "Upload failed. Please try again.",
  payment:      "We couldn't process that payment. Please try again.",
  payment_form: "Oops — looks like the payment form isn’t loading. We’re on it. Come back shortly.",
  message:      "Your message couldn't be sent. Please try again.",
  profile:      "We couldn't save your changes. Please try again.",
  load:         "Something went wrong loading this page. Please refresh.",
  forum:        "We couldn't complete that action. Please try again.",
  booking:      "We couldn't complete your booking. Please try again.",
  generic:      "Something went wrong. Please try again.",
};

/** Technical substrings that should never be shown to end users */
const TECHNICAL_PATTERNS = [
  "Error:",
  "prisma",
  "ECONNREFUSED",
  "ECONNRESET",
  "ENOTFOUND",
  "fetch",
  "undefined",
  "null",
  " at ",        // stack trace line
  "P2",          // Prisma error codes
  "stack",
  "SyntaxError",
  "TypeError",
  "NetworkError",
  "AbortError",
  "JSON",
  "client_secret",
  "setupintent",
  "paymentintent",
  "publishable",
  "secret key",
  "api key",
  "no such",
  "invalid api",
  "livemode",
  "test mode",
  "resource_missing",
  "unauthorized",
  "internal server",
  "not configured",
  "failed to create setup",
  "failed to create payment",
  "server error",
];

function isTechnical(msg: string): boolean {
  const lower = msg.toLowerCase();
  return (
    msg.length > 200 ||
    TECHNICAL_PATTERNS.some((p) => lower.includes(p.toLowerCase()))
  );
}

function isStripeConsumerMessage(msg: string): boolean {
  if (isTechnical(msg)) return false;
  return /your card|card was declined|insufficient funds|security code|incorrect (cvc|number|zip)|invalid (cvc|number)|incomplete|postal code|does not support this type of purchase/i.test(
    msg,
  );
}

/**
 * Maps a Stripe.js error (confirmSetup / confirmPayment / load) to copy
 * that is safe to show. Card and field-validation messages pass through;
 * integration errors (key mismatch, SetupIntent, API) do not.
 */
export function toUserStripeMessage(
  stripeError: { type?: string; message?: string } | null | undefined,
  context: ErrorContext = "payment",
): string {
  const msg = stripeError?.message?.trim();
  if (!msg) return FRIENDLY[context];
  const type = stripeError?.type;
  if (
    (type === "card_error" || type === "validation_error") &&
    !isTechnical(msg)
  ) {
    return msg;
  }
  if (isStripeConsumerMessage(msg)) return msg;
  return FRIENDLY[context];
}

/**
 * Returns a user-friendly string from any caught error value.
 *
 * Stripe card/decline messages are passed through as-is because they are
 * already written for consumers (e.g. "Your card was declined.").
 */
export function toUserMessage(
  err: unknown,
  context: ErrorContext = "generic"
): string {
  if (typeof err === "string") return toUserApiMessage(err, context);
  if (err && typeof err === "object" && "message" in err) {
    const maybeStripe = err as { type?: string; message?: string };
    if (maybeStripe.type) return toUserStripeMessage(maybeStripe, context);
  }
  if (err instanceof Error) {
    if (isStripeConsumerMessage(err.message)) return err.message;
  }
  return FRIENDLY[context];
}

/**
 * Returns a user-friendly string from an API `{ error: string }` payload.
 * Short, clean API validation messages (e.g. "Email already in use") are
 * passed through. Anything that looks technical falls back to FRIENDLY copy.
 */
export function toUserApiMessage(
  apiError: string | undefined | null,
  context: ErrorContext = "generic"
): string {
  if (!apiError) return FRIENDLY[context];
  if (isTechnical(apiError)) return FRIENDLY[context];
  if (isStripeConsumerMessage(apiError)) return apiError;
  return apiError;
}

/** Convenience: friendly fallback string for a context, no error needed */
export function friendlyError(context: ErrorContext = "generic"): string {
  return FRIENDLY[context];
}
